import { ArrowLeft, Menu, Search } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useI18n } from '@/i18n'
import { LanguageSwitcher } from '@/components/LanguageSwitcher'
import {
  backHintForPath,
  fallbackParent,
  getFromState,
  hasSameAppHistory,
} from '@/lib/navHistory'

type Props = {
  onMenu: () => void
  onSearch: () => void
  title?: string
}

export function TopBar({ onMenu, onSearch, title }: Props) {
  const navigate = useNavigate()
  const location = useLocation()
  const { t, lang, langPath } = useI18n()
  const { from, fromLabel } = getFromState(location)
  const isHome = location.pathname === `/${lang}` || location.pathname === `/${lang}/`

  const hint = from
    ? (fromLabel ? (lang === 'en' ? `to ${fromLabel}` : `zu ${fromLabel}`) : backHintForPath(from, lang))
    : ''
  const backLabel = hint
    ? (lang === 'en' ? `Back ${hint}` : `Zurück ${hint}`)
    : t.actions.back

  function goBack() {
    if (from) {
      navigate(from)
      return
    }
    if (hasSameAppHistory()) {
      navigate(-1)
      return
    }
    navigate(fallbackParent(location.pathname, lang))
  }

  return (
    <header className="topbar">
      {!isHome ? (
        <button
          type="button"
          className="btn btn-ghost topbar-back no-print"
          onClick={goBack}
          aria-label={backLabel}
          title={backLabel}
        >
          <ArrowLeft size={18} />
          <span className="topbar-back-label">{backLabel}</span>
        </button>
      ) : (
        <div className="topbar-back-spacer" aria-hidden="true" />
      )}

      <button
        type="button"
        className="btn btn-ghost topbar-menu no-print"
        onClick={onMenu}
        aria-label={t.actions.menu}
      >
        <Menu size={18} />
      </button>

      <div className="topbar-title">
        <strong>{title || `${t.appTitle} - ${t.appSubtitle}`}</strong>
      </div>
      <LanguageSwitcher />
      <button type="button" className="btn no-print" onClick={onSearch}>
        <Search size={16} /> <span className="kbd">Ctrl+K</span>
      </button>
    </header>
  )
}
